// apps/site-dna-web/src/components/retro/RetroDashboardContent.js
// Mission Control center content - metrics, activity log, quick actions
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Snackbar, Slide } from '@mui/material';
import { useAuth, vortxTypography } from '@vortx/shared-ui';
import useMousePosition from '../../../../vortx-shared-ui/src/utils/useMousePosition';
import RetroTaglineRotator from './RetroTaglineRotator';
import PlatformStatusBar from './PlatformStatusBar';
import AppTile from './AppTile';
import ActivityLog from './ActivityLog';
import ConnectionLines from './ConnectionLines';
import ProjectsTerminal from './ProjectsTerminal';
import { API_BASE_URL } from '../../services/api';


const RetroDashboardContent = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { normalizedPos } = useMousePosition();
    const [showHelp, setShowHelp] = useState(false);
    const [activityLog, setActivityLog] = useState([]);

    // State for connection line particles
    const [spawnEvents, setSpawnEvents] = useState([]);
    const containerRef = useRef(null);

    // Metrics state - fetched from real data
    const [siteDnaData, setSiteDnaData] = useState({ projects: 0, bessMwh: 0, savingsM: 0 });
    const [fluxEvData, setFluxEvData] = useState({ projects: 0, vehicles: 0, chargers: 0 });
    const [fluxEmsData, setFluxEmsData] = useState({ systemsOnline: 0, uptime: 0, energyDispatched: 0 });
    const [fluxColdData, setFluxColdData] = useState({ facilities: 0, zonesManaged: 0, energySavedKwh: 0 });
    const [fluxPhdData, setFluxPhdData] = useState({ simulations: 0, reports: 0, avgEfficiency: 0 });

    // Clearance denied notification state
    const [clearanceDenied, setClearanceDenied] = useState(false);
    const [deniedModule] = useState('');

    const spawnParticle = (appIndex, direction) => {
        const appCodes = ['SD', 'FEV', 'FEMS', 'COLD', 'PhD'];
        const appCode = appCodes[appIndex];
        if (!appCode) return;
        setSpawnEvents(prev => [...prev, { appCode, direction, timestamp: Date.now() }]);
    };

    const addActivity = (app, message, type = 'info') => {
        const now = new Date();
        const time = `[${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}]`;

        setActivityLog(prev => {
            const newLog = [{
                id: Date.now(),
                time,
                app,
                message,
                type,
                timestamp: Date.now(),
            }, ...prev];
            return newLog.slice(0, 50); // Keep max 50 entries
        });
    };

    // Fetch recent projects and scenarios to build activity log
    useEffect(() => {
        const fetchActivity = async () => {
            if (!currentUser) {
                return;
            }
            try {
                const token = await currentUser.getIdToken();

                // Fetch projects (already ordered by updated_at desc)
                const projectsRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!projectsRes.ok) {
                    return;
                }

                const projectsData = await projectsRes.json();
                const projects = projectsData.projects || [];

                // Build activity entries from projects and their scenarios
                const activities = [];

                // Add project entries
                for (const project of projects.slice(0, 5)) {
                    // Project activity
                    const projectTime = new Date(project.updated_at || project.created_at);
                    activities.push({
                        id: `project-${project.id}-${projectTime.getTime()}`,
                        time: `[${projectTime.getHours().toString().padStart(2, '0')}:${projectTime.getMinutes().toString().padStart(2, '0')}:${projectTime.getSeconds().toString().padStart(2, '0')}]`,
                        app: 'SD',
                        message: `Project "${project.name}" updated`,
                        type: 'info',
                        timestamp: projectTime.getTime(),
                    });

                    // Fetch scenarios for this project
                    try {
                        const scenariosRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${project.id}/scenarios`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (scenariosRes.ok) {
                            const scenariosData = await scenariosRes.json();
                            const scenarios = scenariosData.scenarios || [];

                            for (const scenario of scenarios.slice(0, 3)) {
                                const hasResults = scenario.reopt_results && Object.keys(scenario.reopt_results).length > 0;
                                const statusText = scenario.status === 'optimized' ? 'optimized' :
                                                   hasResults ? 'has results' : 'updated';
                                const scenarioTime = new Date(scenario.updated_at || scenario.created_at);
                                activities.push({
                                    id: `scenario-${scenario.id}-${scenarioTime.getTime()}`,
                                    time: `[${scenarioTime.getHours().toString().padStart(2, '0')}:${scenarioTime.getMinutes().toString().padStart(2, '0')}:${scenarioTime.getSeconds().toString().padStart(2, '0')}]`,
                                    app: 'SD',
                                    message: `Scenario "${scenario.name}" ${statusText}`,
                                    type: scenario.status === 'optimized' || hasResults ? 'success' : 'info',
                                    timestamp: scenarioTime.getTime(),
                                });
                            }
                        }
                    } catch (err) {
                        console.error(`Failed to fetch scenarios for project ${project.id}`, err);
                    }
                }

                // Sort all activities by timestamp (most recent first)
                activities.sort((a, b) => b.timestamp - a.timestamp);

                // Take top entries
                setActivityLog(activities.slice(0, 50));
            } catch (err) {
                console.error('Failed to fetch activity', err);
            }
        };

        fetchActivity();
    }, [currentUser]);

    // Fetch metrics for dashboard panels
    useEffect(() => {
        const fetchMetrics = async () => {
            if (!currentUser) {
                return;
            }
            try {
                const token = await currentUser.getIdToken();
                const headers = { 'Authorization': `Bearer ${token}` };

                // --- Site DNA: use Site DNA API (includes legacy projects) ---
                const sdProjectsRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects`, { headers });
                const sdProjects = sdProjectsRes.ok
                    ? ((await sdProjectsRes.json()).projects || [])
                    : [];

                let totalBessMwh = 0;
                let totalSavingsK = 0;

                for (const project of sdProjects) {
                    try {
                        const scenariosRes = await fetch(
                            `${API_BASE_URL}/api/site_dna/v1/projects/${project.id}/scenarios`,
                            { headers }
                        );
                        if (scenariosRes.ok) {
                            const scenariosData = await scenariosRes.json();
                            const scenarios = scenariosData.scenarios || [];

                            // Pick the scenario with the highest savings (NPV) per project
                            let bestNpv = 0;
                            let bestStorageKwh = 0;
                            for (const scenario of scenarios) {
                                const summary = scenario.metrics_summary;
                                if (summary) {
                                    const npv = summary.npv || 0;
                                    if (npv > bestNpv) {
                                        bestNpv = npv;
                                        bestStorageKwh = summary.storage_kwh || 0;
                                    }
                                }
                            }
                            totalBessMwh += bestStorageKwh / 1000;
                            totalSavingsK += bestNpv / 1000;
                        }
                    } catch (err) {
                        console.error(`Failed to fetch scenarios for project ${project.id}`, err);
                    }
                }

                const totalSavingsM = totalSavingsK / 1000;
                setSiteDnaData({
                    projects: sdProjects.length,
                    bessMwh: Math.round(totalBessMwh * 10) / 10,
                    savingsM: Math.round(totalSavingsM * 10) / 10
                });

                // --- EV / COLD / EMS: use unified projects API (apps field) ---
                const unifiedRes = await fetch(`${API_BASE_URL}/api/projects/v1/projects`, { headers });
                let unifiedProjects = [];
                if (unifiedRes.ok) {
                    const unifiedJson = await unifiedRes.json();
                    // API returns array directly OR {projects: [...]}
                    unifiedProjects = Array.isArray(unifiedJson) ? unifiedJson : (unifiedJson.projects || []);
                }

                let evProjects = 0, evVehicles = 0, evChargers = 0;
                let coldProjects = 0, coldZones = 0;
                let emsProjects = 0;

                for (const project of unifiedProjects) {
                    const apps = project.apps || {};
                    if (apps.flux_ev?.enabled) {
                        evProjects++;
                        const evConfig = apps.flux_ev.config || {};
                        evVehicles += (evConfig.fleet || []).length;
                        evChargers += (evConfig.chargers || []).length;
                    }

                    if (apps.flux_cold?.enabled) {
                        coldProjects++;
                        const coldConfig = apps.flux_cold.config || {};
                        coldZones += (coldConfig.zones || []).length;
                    }

                    if (apps.flux_ems?.enabled) {
                        emsProjects++;
                    }
                }

                setFluxEvData({ projects: evProjects, vehicles: evVehicles, chargers: evChargers });
                setFluxColdData({ facilities: coldProjects, zonesManaged: coldZones, energySavedKwh: 0 });
                setFluxEmsData({ systemsOnline: emsProjects, uptime: 0, energyDispatched: 0 });

                // Fetch Flux PhD metrics (separate collection, not in unified projects)
                try {
                    const phdSimsRes = await fetch(
                        `${API_BASE_URL}/api/flux_phd/simulations?limit=100`,
                        { headers }
                    );

                    if (phdSimsRes.ok) {
                        const phdSimsData = await phdSimsRes.json();
                        const simulations = phdSimsData.simulations || [];

                        const completedCount = simulations.filter(s => s.status === 'completed').length;

                        const simsWithEfficiency = simulations.filter(s => s.efficiency_pct != null);
                        const avgEfficiency = simsWithEfficiency.length > 0
                            ? Math.round(simsWithEfficiency.reduce((sum, s) => sum + (s.efficiency_pct || 0), 0) / simsWithEfficiency.length * 10) / 10
                            : 0;

                        setFluxPhdData({
                            simulations: simulations.length,
                            reports: completedCount,
                            avgEfficiency: avgEfficiency
                        });
                    }
                } catch (err) {
                    console.error('Failed to fetch Flux PhD metrics', err);
                }

            } catch (err) {
                console.error('Failed to fetch metrics', err);
            }
        };

        fetchMetrics();
    }, [currentUser]);

    // Continuous particle spawning and activity log
    useEffect(() => {
        const dnaInterval = setInterval(() => {
            spawnParticle(0, Math.random() > 0.5 ? 'up' : 'down');
        }, 2000);
        const evInterval = setInterval(() => {
            spawnParticle(1, Math.random() > 0.5 ? 'up' : 'down');
        }, 3000);
        const emsInterval = setInterval(() => {
            spawnParticle(2, Math.random() > 0.5 ? 'up' : 'down');
        }, 5000);
        const coldInterval = setInterval(() => {
            spawnParticle(3, Math.random() > 0.5 ? 'up' : 'down');
        }, 4000);
        const phdInterval = setInterval(() => {
            spawnParticle(4, Math.random() > 0.5 ? 'up' : 'down');
        }, 7000);

        const activityInterval = setInterval(() => {
            const apps = ['DNA', 'EV', 'PhD', 'EMS', 'COLD'];
            const messages = [
                'Data synchronization complete',
                'Metrics updated',
                'Processing optimization',
                'System health check passed',
                'New data received',
                'Cache refreshed',
                'Analytics updated',
            ];
            const types = ['info', 'success', 'upload', 'download'];

            const app = apps[Math.floor(Math.random() * apps.length)];
            const message = messages[Math.floor(Math.random() * messages.length)];
            const type = types[Math.floor(Math.random() * types.length)];

            addActivity(app, message, type);
        }, 5000); // Add activity entry every 5 seconds

        return () => {
            clearInterval(dnaInterval);
            clearInterval(evInterval);
            clearInterval(emsInterval);
            clearInterval(coldInterval);
            clearInterval(phdInterval);
            clearInterval(activityInterval);
        };
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.ctrlKey && e.key === 'n') {
                e.preventDefault();
                navigate('/all-projects');
            } else if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                navigate('/all-projects');
            } else if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                setShowHelp(prev => !prev);
            } else if (e.key === 'Escape' && showHelp) {
                setShowHelp(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigate, showHelp]);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ mb: 3, flexShrink: 0, position: 'relative', zIndex: 1 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    mb: 2,
                }}>
                    <Box sx={{
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(178, 140, 80, 0.5))',
                    }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#b28c50',
                        textShadow: '0 0 20px rgba(178, 140, 80, 0.4)',
                        letterSpacing: '0.3em',
                        textTransform: 'uppercase',
                    }}>
                        Mission Control
                    </Typography>
                    <Box sx={{
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(90deg, rgba(178, 140, 80, 0.5), transparent)',
                    }} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <RetroTaglineRotator />
                </Box>
            </Box>

            {/* Platform Status Bar */}
            <Box sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
                <PlatformStatusBar
                    totalProjects={siteDnaData.projects + fluxEvData.projects}
                    totalEnergyMwh={siteDnaData.bessMwh}
                    totalOptimizations={siteDnaData.projects * 2} // Rough estimate
                    systemsOnline={5}
                />
            </Box>

            {/* App Tiles with Connection Lines */}
            <Box ref={containerRef} sx={{ position: 'relative', mb: 3, pt: 4, zIndex: 1 }}>
                <ConnectionLines
                    statusBarHeight={0}
                    appCodes={['SD', 'FEV', 'FEMS', 'COLD', 'PhD']}
                    spawnEvents={spawnEvents}
                />
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(5, 1fr)',
                    },
                    gap: 2,
                    position: 'relative',
                    zIndex: 2,
                    mt: 3,
                }}>
                    <AppTile
                        appCode="SD"
                        appName="SITE DNA"
                        appColor="#b28c50"
                        onClick={() => spawnParticle(0, 'up')}
                        mousePos={normalizedPos}
                        metrics={[
                            { value: siteDnaData.projects, label: 'PROJECTS' },
                            { value: siteDnaData.bessMwh.toFixed(1), label: 'BESS MWH' },
                            { value: `$${siteDnaData.savingsM}M`, label: 'SAVED' },
                        ]}
                        lastActivityTime={Date.now() - 120000}
                        isActive={siteDnaData.projects > 0}
                    />

                    <AppTile
                        appCode="FEV"
                        appName="FLUX EV"
                        appColor="#4CAF50"
                        onClick={() => spawnParticle(1, 'up')}
                        mousePos={normalizedPos}
                        metrics={[
                            { value: fluxEvData.projects, label: 'PROJECTS' },
                            { value: fluxEvData.vehicles, label: 'VEHICLES' },
                            { value: fluxEvData.chargers, label: 'CHARGERS' },
                        ]}
                        lastActivityTime={Date.now() - 300000}
                        isActive={fluxEvData.projects > 0}
                    />

                    <AppTile
                        appCode="FEMS"
                        appName="FLUX EMS"
                        appColor="#3f6a64"
                        onClick={() => spawnParticle(2, 'up')}
                        mousePos={normalizedPos}
                        metrics={[
                            { value: fluxEmsData.systemsOnline, label: 'SYSTEMS' },
                            { value: `${fluxEmsData.uptime}%`, label: 'UPTIME' },
                            { value: fluxEmsData.energyDispatched, label: 'KWH' },
                        ]}
                        lastActivityTime={null}
                        isActive={fluxEmsData.systemsOnline > 0}
                    />

                    <AppTile
                        appCode="COLD"
                        appName="FLUX COLD"
                        appColor="#2196F3"
                        onClick={() => spawnParticle(3, 'up')}
                        mousePos={normalizedPos}
                        metrics={[
                            { value: fluxColdData.facilities, label: 'FACILITIES' },
                            { value: fluxColdData.zonesManaged, label: 'ZONES' },
                            { value: fluxColdData.energySavedKwh, label: 'KWH SAVED' },
                        ]}
                        lastActivityTime={null}
                        isActive={fluxColdData.facilities > 0}
                    />

                    <AppTile
                        appCode="PhD"
                        appName="FLUX PhD"
                        appColor="#9b59b6"
                        onClick={() => spawnParticle(4, 'up')}
                        mousePos={normalizedPos}
                        metrics={[
                            { value: fluxPhdData.simulations, label: 'SIMS' },
                            { value: fluxPhdData.reports, label: 'COMPLETED' },
                            { value: `${fluxPhdData.avgEfficiency}%`, label: 'AVG EFF' },
                        ]}
                        lastActivityTime={Date.now() - 600000}
                        isActive={fluxPhdData.simulations > 0}
                    />
                </Box>
            </Box>

            {/* Activity Log */}
            <Box sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
                <ActivityLog activities={activityLog} />
            </Box>

            {/* Projects Terminal - Shows recent projects */}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <ProjectsTerminal />
            </Box>

            {/* Help Overlay */}
            {showHelp && (
                <Box
                    onClick={() => setShowHelp(false)}
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.85)',
                        zIndex: 10001,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            background: 'rgba(10, 18, 16, 0.98)',
                            border: '1px solid rgba(178, 140, 80, 0.5)',
                            padding: 4,
                            maxWidth: 500,
                            boxShadow: '0 0 30px rgba(178, 140, 80, 0.2)',
                        }}
                    >
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: '#b28c50',
                            mb: 3,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                        }}>
                            Keyboard Shortcuts
                        </Typography>
                        {[
                            { key: 'Ctrl + N', action: 'Go to projects' },
                            { key: 'Ctrl + P', action: 'Go to projects' },
                            { key: 'Ctrl + D', action: 'Go to dashboard' },
                            { key: '?', action: 'Show this help' },
                            { key: 'Esc', action: 'Close dialogs' },
                        ].map(({ key, action }) => (
                            <Box key={key} sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 1,
                                borderBottom: '1px solid rgba(63, 106, 100, 0.2)',
                            }}>
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.85rem',
                                    color: '#5fd4c4',
                                    padding: '0.2rem 0.5rem',
                                    background: 'rgba(63, 106, 100, 0.2)',
                                    border: '1px solid rgba(63, 106, 100, 0.4)',
                                }}>
                                    {key}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.85rem',
                                    color: '#c8c8c8',
                                }}>
                                    {action}
                                </Typography>
                            </Box>
                        ))}
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: '#666',
                            mt: 3,
                            textAlign: 'center',
                        }}>
                            Press Esc or click outside to close
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Clearance Denied Notification */}
            <Snackbar
                open={clearanceDenied}
                autoHideDuration={3000}
                onClose={() => setClearanceDenied(false)}
                TransitionComponent={Slide}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box
                    sx={{
                        bgcolor: 'rgba(10, 8, 8, 0.98)',
                        border: '1px solid #e74c3c',
                        px: 4,
                        py: 2.5,
                        boxShadow: '0 0 30px rgba(231, 76, 60, 0.4), inset 0 0 30px rgba(231, 76, 60, 0.1)',
                        textAlign: 'center',
                        fontFamily: vortxTypography.mono,
                        position: 'relative',
                        // Corner accents
                        '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            width: '12px',
                            height: '12px',
                            borderColor: '#e74c3c',
                        },
                        '&::before': {
                            top: 4,
                            left: 4,
                            borderTop: '2px solid',
                            borderLeft: '2px solid',
                        },
                        '&::after': {
                            bottom: 4,
                            right: 4,
                            borderBottom: '2px solid',
                            borderRight: '2px solid',
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: '#e74c3c',
                            letterSpacing: '0.25em',
                            textTransform: 'uppercase',
                            textShadow: '0 0 15px rgba(231, 76, 60, 0.8)',
                            mb: 0.5,
                        }}
                    >
                        ⚠ CLEARANCE DENIED ⚠
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: 'rgba(231, 76, 60, 0.7)',
                            letterSpacing: '0.1em',
                        }}
                    >
                        {deniedModule} access not granted
                    </Typography>
                </Box>
            </Snackbar>
        </Box>
    );
};

export default RetroDashboardContent;
